import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import BaseModelNameDisplayer from "components/BaseModelNameDisplayer";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import EvaluationPreview from "components/EvaluationPreview";
import IconQuesionType from "components/IconQuestionType";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import RedirectBackButton from "components/RedirectBackButton";
import SortableTable from "components/SortableTable";
import TruncatedQuestion from "components/TruncatedQuestion";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import PrintReferenceDisplayer from "pages/EvaluationsPage/PrintReferenceDisplayer";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Question, ResponseOfEvaluation } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { QuestionTypeEnum } from "types/common-types";

interface QuestionWithId extends Question {
  id: string;
  questionNumber: number;
}

const EvaluationDetails = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerPromise } = useTrashablePromises();
  const [evaluationPreviewOpen, setEvaluationPreviewOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const {
    evaluationsUrl,
    evaluationEditUrl,
    evaluationService,
    productService,
    skuService,
    questionDetailsUrl,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { formatMessage: t } = useIntl();

  const { organizationId } = contextStore.useState();

  const { isLoading, isError, data, error } = useQuery<ResponseOfEvaluation>(
    ["evaluation", id],
    () => evaluationService.getEvaluationById(id!),
    {
      enabled: !!id,
    }
  );

  const evaluation = data?.data;
  useTitle(evaluation?.name || "Evaluation Details");

  if (!id) return <Navigate to={evaluationsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    console.log(error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="evaluationLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    await registerPromise(evaluationService.disableEvaluation(id));
    queryClient.invalidateQueries("evaluations");
    navigate(evaluationsUrl());
  };

  // Navigate to the list if this evaluation doesn't belong in this context
  if (!data?.data) {
    return <Navigate to={evaluationsUrl()} replace />;
  }

  const evaluationOrganizationId = evaluation?.organizationId ?? organizationId;

  const {
    description,
    organizationName,
    skuId,
    evaluationTypeId,
    productTypeId,
    skuAvailabilityId,
    productId,
    productYearId,
    productVersionId,
    attemptsAllowed,
    passingScore,
    daysAfterCourse,
    isActive,
    enablePrintReference,
    hasConfidenceLevels,
  } = evaluation!;

  // The BaseModelNameDisplayer component assumes that the field to display is called 'name'.
  // In the case of skus it's called 'sku'.  Rather than create a specific component just for this
  // case, we massage the data a little...
  const skuFetcher = async () => {
    const skuResult = await skuService.getSkuById(skuId!);
    return {
      ...skuResult,
      data: { ...skuResult.data, name: skuResult.data?.sku },
    };
  };

  const rows = [
    {
      title: t({ id: "descriptionLabel" }),
      component: description && <span>{description}</span>,
    },

    {
      title: t({ id: "organization" }),
      component: organizationName && <span>{organizationName}</span>,
    },

    {
      title: t({ id: "evaluationFormEvaluationTypeLabel" }),
      component: evaluationTypeId && (
        <BaseModelNameDisplayer
          id={evaluationTypeId}
          cacheKey={["evaluationType", evaluationTypeId]}
          fetcher={() =>
            evaluationService.getEvaluationTypeById(evaluationTypeId)
          }
        />
      ),
    },

    {
      title: t({ id: "evaluationFormSkuLabel" }),
      component: skuId && (
        <BaseModelNameDisplayer
          id={skuId}
          cacheKey={["sku", skuId]}
          fetcher={skuFetcher}
        />
      ),
    },

    {
      title: t({ id: "evaluationFormProductLabel" }),
      component: productId && (
        <BaseModelNameDisplayer
          id={productId}
          cacheKey={["product", productId]}
          fetcher={async () => {
            const product = await productService.getProductById(productId);
            if (product.data) {
              product.data.name = product.data.computedName;
            }
            return product;
          }}
        />
      ),
    },

    {
      title: t({ id: "skuAvailabilityLabel" }),
      component: skuAvailabilityId && (
        <BaseModelNameDisplayer
          id={skuAvailabilityId}
          cacheKey={["SkuAvailabilities", skuAvailabilityId]}
          fetcher={() => skuService.getSkuAvailabilityById(skuAvailabilityId)}
        />
      ),
    },

    {
      title: t({ id: "evaluationFormProductTypeLabel" }),
      component: productTypeId && (
        <BaseModelNameDisplayer
          id={productTypeId}
          cacheKey={["productTypes", productTypeId]}
          fetcher={() => productService.getProductTypeById(productTypeId)}
        />
      ),
    },

    {
      title: t({ id: "evaluationFormProductVersionLabel" }),
      component: productVersionId && (
        <BaseModelNameDisplayer
          id={productVersionId}
          cacheKey={["productVersions", productVersionId]}
          fetcher={() => productService.getProductVersionById(productVersionId)}
        />
      ),
    },

    {
      title: t({ id: "evaluationFormProductYearLabel" }),
      component: productYearId && (
        <BaseModelNameDisplayer
          id={productYearId}
          cacheKey={["productYear", productYearId]}
          fetcher={() => productService.getProductYearById(productYearId)}
        />
      ),
    },

    {
      title: t({ id: "evaluationFormAttemptsAllowedLabel" }),
      component: attemptsAllowed && <span>{attemptsAllowed}</span>,
    },

    {
      title: t({ id: "passingScoreLabel" }),
      component: passingScore && <span>{passingScore}</span>,
    },

    {
      title: t({ id: "evaluationFormDayAfterCourseLabel" }),
      component: daysAfterCourse && <span>{daysAfterCourse}</span>,
    },
    {
      title: t({ id: "evaluationFormActiveLabel" }),
      component: isActive ? <CheckIcon /> : <CloseIcon />,
    },
    {
      title: t({ id: "evaluationEnableReferenceLabel" }),
      component: enablePrintReference != null && (
        <PrintReferenceDisplayer
          enablePrintReference={enablePrintReference}
          organizationId={evaluationOrganizationId!}
        />
      ),
    },
    {
      title: t({ id: "addConfidenceLevelsLabel" }),
      component: hasConfidenceLevels ? <CheckIcon /> : <CloseIcon />,
    },
  ].filter((r) => !!r.component);

  const questions =
    evaluation?.evaluationQuestions?.map(
      (eq, i) =>
        ({
          ...eq.question,
          questionNumber: i + 1,
        } as QuestionWithId)
    ) ?? [];

  const RenderCorrectAnswer = ({ value: question }: { value: Question }) => {
    const isTxtQuestion =
      question.questionType?.value === QuestionTypeEnum.TextQuestion;
    return (
      <>
        {isTxtQuestion && question.evaluationQuestionAnswers?.length ? (
          <div
            dangerouslySetInnerHTML={{
              __html: question.evaluationQuestionAnswers[0].value!,
            }}
          />
        ) : (
          question.correctAnswersLetters
        )}
      </>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "questionNumber",
      type: "text",
      label: "#",
      noSort: true,
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      View: TruncatedQuestion,
      label: t({ id: "questionLabel" }),
      noSort: true,
    },
    {
      accessor: "questionType.name",
      align: "center",
      width: 0,
      type: "node",
      View: IconQuesionType,
      label: t({ id: "questionsTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "required",
      align: "center",
      width: 0,
      type: "boolean",
      label: t({ id: "questionsRequiredLabel" }),
      noSort: true,
    },
    {
      accessor: "correctAnswersLetters",
      align: "left",
      width: 0,
      type: "node",
      View: RenderCorrectAnswer,
      label: t({ id: "correctAnswerLabel" }),
      noSort: true,
    },
  ];

  const onClick = (id: string) => {
    navigate(questionDetailsUrl(id));
  };

  const backButtonProps = location.state?.backButtonProps;

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={evaluationsUrl()}
          labelKey="backToListButtonLabel"
          {...backButtonProps}
        />
      </Box>
      <Typography variant="h4">{evaluation?.name}</Typography>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={evaluationEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab color="primary" onClick={() => setEvaluationPreviewOpen(true)}>
          <Tooltip title={t({ id: "previewEvaluationLabel" })}>
            <VisibilityIcon />
          </Tooltip>
        </Fab>
        <Box ml={4} />
        <Fab color="secondary" onClick={deleteOpenActions.setTrue}>
          <DeleteIcon />
        </Fab>
      </Box>

      <Box my={3}>
        <TableContainer>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <b>{title}</b>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <SortableTable
        {...{
          rows: questions,

          dense: true,
          title: t({ id: "evaluationFormQuestionsLabel" }),
          cells,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => questionDetailsUrl(id)),
          ],
        }}
        defaultOrderBy="sequence"
        useRowIndexesAsReactKey={true}
      />

      <DeleteEntityDialog
        dialogTitleKey="evaluationDeleteTitle"
        contentKey="evaluationDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <EvaluationPreview
        open={evaluationPreviewOpen}
        title={evaluation!.name}
        questions={
          (evaluation!.evaluationQuestions?.map(
            (x) => x.question
          ) as Question[]) ?? []
        }
        onClose={() => setEvaluationPreviewOpen(false)}
      />
    </>
  );
};

export default EvaluationDetails;
